import { PlusCircleOutlined, DeleteOutlined, EditOutlined, BranchesOutlined } from '@ant-design/icons'
import { Button, DatePicker, Menu, message, Modal, Spin, Dropdown } from 'antd'
import { MissionCreateModal } from '../MissionCreatePage/MissionCreatePageStyled'
import { toJS } from 'mobx'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet/es/Helmet'
import { useHistory, useLocation } from 'react-router-dom'
import queryString from 'query-string'
import AccessControlAction from '../../components/AccessControlAction'
import AppPageHeader from '../../components/AppPageHeader'
import ContentBlockWrapper from '../../components/ContentBlockWrapper'
import { systemName } from '../../config'
import { blue } from '../../color'
import {
  ACL_ACTION_TYPE,
  ISO_DATE_FORMAT,
  WARNING_MESSAGE,
} from '../../constants'
import DashboardLayout from '../../layouts/DashboardLayout'
import {
  buildImsatWorkScheduleCompanies,
  findSelectedImsatCompany,
  getImsatCpctmCodesToHide,
  getMergedLocalImsatConfig,
  mergeWorkSchedules,
} from '../../utils/imsatWorkScheduleUtils'
import { WorkScheduleRequest } from '../../requests/WorkScheduleRequest'
import OfficeCalendar from './OfficeCalendar'
import { OfficeCalendarEventDetail } from './OfficeCalendarEventDetail'

const CompanyWorkScheduleListPageV2 = props => {
  const {
    workScheduleStore,
    parentWorkScheduleStore,
    authenticationStore,
    companyStore,
    aclStore,
    infoOptionStore,
  } = props
  const { isPublic } = props

  const history = useHistory()
  const location = useLocation()

  const { currentUser } = authenticationStore
  const { companyList } = companyStore
  const { aclActionsByUser } = aclStore

  const { CODE: optionCode } = infoOptionStore.infoOptionList || {}
  const permissionViewList = toJS(workScheduleStore.workSchedulePermissionView)

  // ─── URL Query Params ────────────────────────────────────────────────────────
  const queryParams = useMemo(
    () =>
      queryString.parse(location.search, {
        arrayFormat: 'index',
      }),
    [location.search]
  )

  const selectedDate = queryParams.date || moment().format(ISO_DATE_FORMAT)
  const selectedKey = queryParams.company || currentUser?.company?.code || ''
  const selectedEventId = queryParams.eventId || null

  // Memo hóa moment value để tránh DatePicker infinite loop
  const selectedDateMoment = useMemo(() => moment(selectedDate), [selectedDate])

  const updateQueryParams = useCallback(
    newParams => {
      const currentParams = queryString.parse(location.search, {
        arrayFormat: 'index',
      })
      const merged = { ...currentParams, ...newParams }
      history.replace({
        search: queryString.stringify(merged, { arrayFormat: 'index' }),
      })
    },
    [location.search, history]
  )

  // ─── IMSAT Config ────────────────────────────────────────────────────────────
  const imsatCompanies = buildImsatWorkScheduleCompanies(
    infoOptionStore.infoOptionList,
    companyList,
    permissionViewList,
    currentUser?.company?.code
  )
  const mergedLocalImsatConfig = getMergedLocalImsatConfig(
    infoOptionStore.infoOptionList,
    companyList,
    selectedKey,
    currentUser?.company?.code
  )
  const selectedImsatCompany = findSelectedImsatCompany(
    imsatCompanies,
    selectedKey
  )
  const isImsatCompanySelected = !!selectedImsatCompany
  const isParentCompanySelected =
    isImsatCompanySelected || selectedKey === optionCode

  // ─── State ───────────────────────────────────────────────────────────────────
  const [isFetchingData, setIsFetchingData] = useState(false)
  const [isSubmittingAction, setIsSubmittingAction] = useState(false)
  const [weeklyWorkSchedule, setWeeklyWorkSchedule] = useState([])
  const [workScheduleList, setWorkScheduledList] = useState([])
  const [loadedWeekStart, setLoadedWeekStart] = useState(null)
  const [loadedCompany, setLoadedCompany] = useState(null)
  const [workScheduleDetail, setWorkScheduleDetail] = useState(null)

  // Refs giữ các giá trị không ổn định (object mới mỗi render từ MobX store)
  const imsatRef = useRef({})
  imsatRef.current = {
    mergedLocalImsatConfig,
    isImsatCompanySelected,
    isParentCompanySelected,
    selectedImsatCompany,
  }

  // Ref giữ weekly cache để không đưᨿa vào deps
  const weeklyWorkScheduleRef = useRef([])
  const loadedWeekStartRef = useRef(null)
  const loadedCompanyRef = useRef(null)

  // ─── Init URL defaults ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!queryParams.company && currentUser?.company?.code) {
      updateQueryParams({ company: currentUser.company.code })
    }
  }, [currentUser?.company?.code]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!queryParams.date) {
      updateQueryParams({ date: moment().format(ISO_DATE_FORMAT) })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ─── ACL Check ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (aclActionsByUser && aclActionsByUser.length) {
      const isAccessWorkSchedule = aclActionsByUser.some(
        action =>
          action.code === ACL_ACTION_TYPE.menu__WORK_SCHEDULE &&
          action.status === true
      )
      if (!isAccessWorkSchedule) {
        message.error(WARNING_MESSAGE.ACCESS_PAGE, 3)
        history.push('/dashboard')
      }
    }
  }, [aclActionsByUser])

  // ─── Permission View ─────────────────────────────────────────────────────────
  useEffect(() => {
    workScheduleStore.getWorkSchedulePermissionView()
  }, [])

  // ─── Fetch Work Schedule ─────────────────────────────────────────────────────
  const handleGetWorkSchedule = useCallback(
    async (forceRefresh = false) => {
      if (!selectedDate || !selectedKey) return

      const startOfWeek = moment(selectedDate)
        .startOf('isoWeek')
        .format(ISO_DATE_FORMAT)
      const endOfWeek = moment(selectedDate)
        .endOf('isoWeek')
        .format(ISO_DATE_FORMAT)

      const {
        mergedLocalImsatConfig: imsatConfig,
        isImsatCompanySelected: isImsat,
        isParentCompanySelected: isParent,
        selectedImsatCompany: imsatCompany,
      } = imsatRef.current

      // Cache hit: same week + same company → filter from cached weekly data
      if (
        !forceRefresh &&
        loadedWeekStartRef.current === startOfWeek &&
        loadedCompanyRef.current === selectedKey
      ) {
        const activeDayData = weeklyWorkScheduleRef.current.filter(
          item =>
            moment(item.start_at).format(ISO_DATE_FORMAT) === selectedDate
        )
        setWorkScheduledList(activeDayData)
        return
      }

      try {
        setIsFetchingData(true)
        let allData = []

        if (imsatConfig) {
          const localData = await workScheduleStore.getWorkSchedule(
            startOfWeek,
            endOfWeek,
            true,
            selectedKey
          )
          let imsatData = []
          try {
            imsatData = await parentWorkScheduleStore.getWorkSchedule(
              startOfWeek,
              endOfWeek,
              imsatConfig.imsatCode,
              imsatConfig.apiUrl
            )
          } catch (imsatError) {
            console.warn('Không lấy được lịch IMSAT cho công ty chính', imsatError)
          }
          allData = mergeWorkSchedules(localData, imsatData)
        } else if (isImsat) {
          allData = await parentWorkScheduleStore.getWorkSchedule(
            startOfWeek,
            endOfWeek,
            imsatCompany.code,
            imsatCompany.apiUrl
          )
        } else if (isParent) {
          allData = await parentWorkScheduleStore.getWorkSchedule(
            startOfWeek,
            endOfWeek
          )
        } else if (isPublic) {
          allData = await workScheduleStore.getPublicWorkSchedule(
            startOfWeek,
            endOfWeek
          )
        } else {
          allData = await workScheduleStore.getWorkSchedule(
            startOfWeek,
            endOfWeek,
            true,
            selectedKey
          )
        }

        weeklyWorkScheduleRef.current = allData
        loadedWeekStartRef.current = startOfWeek
        loadedCompanyRef.current = selectedKey
        setWeeklyWorkSchedule(allData)
        setLoadedWeekStart(startOfWeek)
        setLoadedCompany(selectedKey)

        const activeDayData = allData.filter(
          item =>
            moment(item.start_at).format(ISO_DATE_FORMAT) === selectedDate
        )
        setWorkScheduledList(activeDayData)
      } catch (err) {
        console.log(err)
        message.error(err.vi || 'Đã có lỗi xảy ra!')
        weeklyWorkScheduleRef.current = []
        loadedWeekStartRef.current = null
        loadedCompanyRef.current = null
        setWeeklyWorkSchedule([])
        setWorkScheduledList([])
        setLoadedWeekStart(null)
        setLoadedCompany(null)
      } finally {
        setIsFetchingData(false)
      }
    },
    // Chỉ dep vào giá trị nguyên thủy ổn định — không để IMSAT objects vào đây
    [selectedDate, selectedKey, isPublic] // eslint-disable-line react-hooks/exhaustive-deps
  )

  // ─── Fetch Event Detail ───────────────────────────────────────────────────────
  const handleGetWorkScheduleDetail = useCallback(async () => {
    if (!selectedEventId) return
    try {
      setIsFetchingData(true)

      const eventInfo =
        weeklyWorkScheduleRef.current?.find(
          e => e.id === selectedEventId || e.schedule_code === selectedEventId
        ) || {}

      const {
        isImsatCompanySelected: isImsat,
        isParentCompanySelected: isParent,
      } = imsatRef.current

      const isImsatEvent = eventInfo._isImsatSource || isParent || isImsat

      let response
      if (isImsatEvent) {
        response = await parentWorkScheduleStore.getWorkScheduleDetail(
          selectedEventId
        )
      } else {
        response = await workScheduleStore.getWorkScheduleDetail(
          selectedEventId
        )
      }
      
      setWorkScheduleDetail(response?.data || null)
    } catch (err) {
      console.log(err)
      message.error(err.vi || 'Đã có lỗi xảy ra!')
      setWorkScheduleDetail(null)
    } finally {
      setIsFetchingData(false)
    }
  }, [selectedEventId, parentWorkScheduleStore, workScheduleStore])

  useEffect(() => {
    handleGetWorkSchedule()
  }, [handleGetWorkSchedule])

  useEffect(() => {
    handleGetWorkScheduleDetail()
  }, [handleGetWorkScheduleDetail])

  // ─── Event Handlers ───────────────────────────────────────────────────────────
  const handleSelectEvent = useCallback(
    event => {
      updateQueryParams({ eventId: event?.id ?? '' })
    },
    [updateQueryParams]
  )

  const handleChangeDate = useCallback(
    date => {
      setWorkScheduleDetail(null)
      updateQueryParams({ date: date?.format(ISO_DATE_FORMAT) })
    },
    [updateQueryParams]
  )

  const changeCategory = useCallback(
    e => {
      // Reset cache when switching company
      setLoadedWeekStart(null)
      setLoadedCompany(null)
      setWeeklyWorkSchedule([])
      setWorkScheduleDetail(null)
      updateQueryParams({ company: e.key, eventId: '' })
    },
    [updateQueryParams]
  )

  const handleDeleteEvent = async schedule_code => {
    Modal.confirm({
      title: 'Xóa sự kiện',
      content: 'Bạn có chắc chắn muốn xóa sự kiện này?',
      okText: 'Đồng ý',
      cancelText: 'Hủy',
      className: 'delete-event-modal',
      onOk: async () => {
        try {
          setIsSubmittingAction(true)
          await WorkScheduleRequest.delete(schedule_code)
          message.success('Xóa sự kiện thành công!')
          updateQueryParams({ eventId: '' })
          setWorkScheduleDetail(null)
          handleGetWorkSchedule(true)
        } catch (err) {
          console.log(err)
          message.error(err.vi || 'Đã có lỗi xảy ra!')
        } finally {
          setIsSubmittingAction(false)
        }
      },
      okButtonProps: {
        danger: true,
        loading: isSubmittingAction,
        style: { borderRadius: 6 }
      },
      cancelButtonProps: {
        disabled: isSubmittingAction,
        style: { borderRadius: 6 }
      },
    })
  }

  const handleEditEvent = schedule_code => {
    history.push({
      pathname: `/company-work-schedule/view/${schedule_code}`,
      search: `?mode=edit_form&date=${selectedDate}&company=${selectedKey}`,
    })
  }

  // ─── Menu Items ───────────────────────────────────────────────────────────────
  const renderMenuItems = () => {
    const imsatCpctmCodesToHide = getImsatCpctmCodesToHide(imsatCompanies)
    let currentUserCompanyMenuItem = null

    const menuItems = companyList
      .filter(company => !imsatCpctmCodesToHide.has(company.code))
      .map(company => {
        const matchingPermission = permissionViewList.find(
          permission => permission.access_type === company.code
        )

        if (matchingPermission) {
          if (
            company.code === currentUser?.company.code &&
            !currentUserCompanyMenuItem
          ) {
            currentUserCompanyMenuItem = (
              <Menu.Item key={company.code}>{company.name}</Menu.Item>
            )
          } else {
            return <Menu.Item key={company.code}>{company.name}</Menu.Item>
          }
        }
        return null
      })

    if (currentUserCompanyMenuItem) {
      menuItems.unshift(currentUserCompanyMenuItem)
    }

    // Thêm các công ty IMSAT vào menu
    imsatCompanies.forEach(ic => {
      menuItems.push(
        <Menu.Item key={ic.code}>{ic.name}</Menu.Item>
      )
    })

    return menuItems
  }

  const isAccessCreate =
    !isParentCompanySelected && selectedKey === currentUser?.company.code

  const menuAction = (
    <Menu>
      {aclActionsByUser?.some(action => action.code === ACL_ACTION_TYPE.edit__WORK_SCHEDULE && action.status) && (
        <Menu.Item
          key="edit"
          onClick={() => handleEditEvent(workScheduleDetail?.schedule_code)}
          icon={<EditOutlined style={{ color: blue }} />}>
          <span style={{ color: blue }}>Chỉnh sửa</span>
        </Menu.Item>
      )}
      {aclActionsByUser?.some(action => action.code === ACL_ACTION_TYPE.delete__WORK_SCHEDULE && action.status) && (
        <Menu.Item
          key="delete"
          danger
          onClick={() => handleDeleteEvent(workScheduleDetail?.schedule_code)}
          icon={<DeleteOutlined />}>
          Xóa
        </Menu.Item>
      )}
    </Menu>
  )

  return (
    <DashboardLayout title={'Link People - Link the World'}>
      <Helmet>
        <title>Lịch cơ quan | {systemName} Portal</title>
      </Helmet>
      <style>{`
        .delete-event-modal .ant-modal-content {
          border-radius: 12px !important;
        }
      `}</style>
      <AppPageHeader
        title="Lịch cơ quan"
        addon={
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <DatePicker
              size="large"
              picker="week"
              onChange={handleChangeDate}
              placeholder={'Chọn tuần'}
              format={date => `Tuần ${date.isoWeek()}, ${date.year()}`}
              value={selectedDateMoment}
              style={{ width: 160, borderRadius: 8 }}
              allowClear={false}
            />
            <AccessControlAction
              aclActionType={ACL_ACTION_TYPE.create__WORK_SCHEDULE}>
              <Button
                size="large"
                icon={<PlusCircleOutlined />}
                onClick={() => history.push('/company-work-schedule/create')}
                type={'primary'}
                style={{ borderRadius: 8 }}
                disabled={!isAccessCreate}>
                Tạo sự kiện mới
              </Button>
            </AccessControlAction>
          </div>
        }
      />

      <Spin spinning={isFetchingData}>
        <ContentBlockWrapper style={{ minHeight: 600 }}>
          <Menu
            className="custom-menu-tab"
            mode="horizontal"
            selectedKeys={[selectedKey]}
            style={{ marginBottom: 16 }}
            onClick={changeCategory}>
            {renderMenuItems()}
          </Menu>
          <OfficeCalendar
            selectedDate={selectedDate}
            data={weeklyWorkSchedule}
            selectedEventId={selectedEventId}
            onEventClick={handleSelectEvent}
            isShowPopoverContent={false}
          />

          <MissionCreateModal
            title="Chi tiết lịch cơ quan"
            visible={Boolean(selectedEventId && workScheduleDetail)}
            onCancel={() => handleSelectEvent(null)}
            footer={null}
            width={1000}
            destroyOnClose
            centered
          >
            <div
              style={{
                background: '#fff',
                borderRadius: '12px',
                padding: '24px',
                maxHeight: 'calc(100vh - 180px)',
                overflowY: 'auto',
              }}>
              <OfficeCalendarEventDetail
                onDeleteEvent={handleDeleteEvent}
                onEditEvent={handleEditEvent}
                workScheduleDetail={workScheduleDetail}
              />
            </div>
          </MissionCreateModal>
        </ContentBlockWrapper>
      </Spin>
    </DashboardLayout>
  )
}

export default inject(
  'workScheduleStore',
  'parentWorkScheduleStore',
  'authenticationStore',
  'companyStore',
  'aclStore',
  'infoOptionStore'
)(observer(CompanyWorkScheduleListPageV2))
